/** @file Applies an autorig plan (plain commands) as one command and records its confidence. */
import { z } from 'zod';
import { ERROR_CODES } from '@pose2d/format/errorCodes';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import type { ErrorCode, Id } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import { applyNested } from './applyNested';
import { createdByKind } from './createdByKind';

const CODES = Object.keys(ERROR_CODES) as [ErrorCode, ...ErrorCode[]];
const warningSchema = z
  .object({
    code: z.union([z.enum(CODES), z.literal('INFO')]),
    message: z.string(),
    params: z.record(z.string(), z.union([z.string(), z.number()])).exactOptional(),
    path: z.string().exactOptional(),
  })
  .strict();

/** autorig.applyPlan: every plan command through ctx.lookup as one undo step; the result carries the plan confidence. */
export const command = defineCommand({
  name: 'autorig.applyPlan',
  description:
    'Apply an autorig plan { commands, confidence 0..1, warnings? } exactly as the autorig tool returned it: every command runs in order as one undo step, the result carries the plan confidence and its warnings, and `created` keys a name several kinds share as <kind>:<name>; a plan cannot contain autorig.applyPlan, project.rename, project.trash or project.restore.',
  input: z
    .object({
      plan: z
        .object({
          // W2-28 cap; applyNested re-indexes per command, so cost grows ~n² (1000 commands ≈ 4 s, 5000 far more).
          commands: z.array(z.object({ name: z.string().min(1), input: z.unknown() }).strict()).min(1).max(5000),
          confidence: prim.unit,
          warnings: z.array(warningSchema).default([]),
        })
        .strict(),
    })
    .strict(),
  apply(draft, input, ctx) {
    const created: [string, Id][] = [];
    input.plan.commands.forEach((call, i) => {
      for (const entry of Object.entries(applyNested(draft, call, ctx, `plan.commands.${i}`))) created.push(entry);
    });
    Object.assign(ctx.created, createdByKind(created));
    for (const w of input.plan.warnings) ctx.warnings.push(w);
    ctx.confidence = input.plan.confidence;
    // No stamp here: each nested command stamps when it writes, so a plan of no-ops changes nothing (R2-13).
  },
});
